import React, {useMemo} from "react";
import {Link} from "react-router-dom";
import {connect} from "react-redux";
import {Text} from "@reside/ui";

import {
  CellFooter,
  CellHeader,
  CellTextBig,
  DashboardCell,
  AdmissionId,
} from "../../../../dashboard-cell";
import {AdmissionFromSearch} from "../../../../../services/AdmissionsService";
import {RelativeDate} from "../../../../relative-date";
import {PdfUploadStatusIcon} from "../../pdf-upload-status-icon";
import {getAdmissionResidentName, getResidentTestId} from "../../../helper";
import {SplitButton} from "../../../../split-button";

import {AdmissionChecklist} from "../admission-checklist";
import {DownloadPDFDropdownItem} from "../DownloadPDFDropdownItem";
import {DownloadArbitrationPDFDropdownItem} from "../DownloadArbitrationPDFDropdownItem";
import {DownloadPartialPDFDropdownItem} from "./DownloadPartialPDFDropdownItem";
import {DeleteAdmissionDropdownItem} from "../DeleteAdmissionDropdownItem";
import {ArchiveAdmissionButton} from "./ArchiveAdmissionButton";
import {AlignRight} from "./styles";
import {PacketSizeLimitAlertIcon} from "../../packet-size-limit-alert-icon/PacketSizeLimitAlertIcon";
import {select} from "../../../../../store";
import {shouldShowPacketSizeLimitAlert} from "./helpers";
import {StarAdmissionIcon} from "../../../../star-icon/StarAdmissionIcon";
import {ForceDeleteAdmissionDropdownItem} from "../ForceDeleteAdmissionDropdownItem";

type Props = Readonly<{
  admission: AdmissionFromSearch;
  hasShowLargeAdmissionWarningFlag?: boolean;
  isResideAdmin: boolean;
}>;

export const AdmissionApprovedCellRenderer = ({
  admission,
  hasShowLargeAdmissionWarningFlag,
  isResideAdmin,
}: Props) => {
  const showPacketSizeLimitAlert = useMemo(
    () =>
      shouldShowPacketSizeLimitAlert(
        admission,
        isResideAdmin,
        hasShowLargeAdmissionWarningFlag,
      ),
    [admission, hasShowLargeAdmissionWarningFlag, isResideAdmin],
  );

  return (
    <DashboardCell withWarningBorder={showPacketSizeLimitAlert}>
      <CellHeader>
        <StarAdmissionIcon
          admissionId={admission.id}
          isStarred={admission.starred}
        />
        <RelativeDate date={admission.updated} />
        <SplitButton
          size="small"
          defaultOptions={<ArchiveAdmissionButton admission={admission} />}
        >
          <DownloadPDFDropdownItem admission={admission} />
          <DownloadArbitrationPDFDropdownItem admission={admission} />
          <DownloadPartialPDFDropdownItem admission={admission} />
          <DeleteAdmissionDropdownItem admission={admission} />
          <ForceDeleteAdmissionDropdownItem admission={admission} />
        </SplitButton>
      </CellHeader>
      <CellTextBig data-test-id={getResidentTestId(admission, "editProfile")}>
        <Link to={`/admin/admissions/${admission.id}`}>
          <Text>{getAdmissionResidentName(admission)}</Text>
        </Link>
      </CellTextBig>
      <AdmissionId code={admission.code} />
      <AdmissionChecklist admission={admission} />
      <CellFooter>
        <AlignRight>
          {admission.resources?.finalPdf?.pccUploadStatus && (
            <PdfUploadStatusIcon
              pdfUploadStatus={admission.resources.finalPdf.pccUploadStatus}
            />
          )}
          {showPacketSizeLimitAlert && <PacketSizeLimitAlertIcon />}
        </AlignRight>
      </CellFooter>
    </DashboardCell>
  );
};

const mapState = (state: any) => ({
  hasShowLargeAdmissionWarningFlag:
    select.adminSession.hasShowLargeAdmissionWarningFlag(state),
  isResideAdmin: select.adminSession.isResideAdmin(state),
});

export const AdmissionApprovedCell = connect(mapState)(
  AdmissionApprovedCellRenderer,
);
